<template>
  <div
    ref="headerRef"
    class="ui-grid__header"
    :data-cell="isCell"
    :data-checked="isDataChecked"
    :class="mainClasses"
  >
    <div class="ui-grid__header-value" :class="valueClasses">
      <slot v-bind="{ columnName, displayValue }">
        {{ displayValue }}
      </slot>
    </div>
    <HeaderSort v-if="isSortVisible" v-model="sort" v-bind="{ column }" />
    <div
      v-if="isResizerVisible"
      ref="resizerRef"
      class="ui-grid__header-resizer"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, useTemplateRef } from 'vue'

import { Sort } from '@types'

import { GridColumn } from '../utils/types'

import useResizeService from '@/components/hooks/resize'

import HeaderSort from './HeaderSort.vue'

type Props = {
  column?: GridColumn
  isFirst: boolean
  isLast?: boolean
  isHeaderChecked?: boolean
}

type Emits = {
  resize: [column: string, width: number]
  reset: [column: string]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'GridHeader',
})

const sort = defineModel<Sort[]>('sort')

const disabled = inject('disabled', false)

const headerRef = useTemplateRef('headerRef')
const resizerRef = useTemplateRef('resizerRef')

const resetTrigger = ref(false)

const columnName = computed(() => props.column?.name)
const displayValue = computed(() => props.column?.caption)

const isSortVisible = computed(
  () => !disabled && props.column && !props.column.unsortable,
)

const isResizerVisible = computed(
  () => !disabled && props.column && !props.column.unresizeable,
)

const mainClasses = computed(() => ({
  'ui-grid__header--checkbox': !props.column,
  'ui-grid__header--first': props.isFirst,
  'ui-grid__header--last': props.isLast,
  [`${props.column?.headerClasses}`]: props.column?.headerClasses,
}))

const valueClasses = computed(() => ({
  [`${props.column?.headerValueClasses}`]: props.column?.headerValueClasses,
}))

const isCell = computed(() =>
  disabled || !props.column || props.column.silent ? undefined : 'true',
)
const isDataChecked = computed(() =>
  !isCell.value || !props.isHeaderChecked ? undefined : 'true',
)

const hanldeResize = (width: number) => {
  if (!props.column) return
  emit('resize', props.column.name, width)
}

const handleReset = () => {
  if (!props.column) return
  resetTrigger.value = true
  emit('reset', props.column.name)
  resetTrigger.value = false
}

if (!disabled && props.column) {
  useResizeService(headerRef, resizerRef, hanldeResize, handleReset)
}
</script>

<style>
.ui-grid__header {
  @apply min-h-[3.275rem];
  @apply sticky;
  @apply top-0;
  @apply flex items-center;
  @apply px-2 py-0.5;
  @apply bg-grid-header;
  @apply border-b border-common-border;
  @apply z-30;

  &--checkbox {
    @apply sticky left-0 z-40;
  }

  &[data-selected] {
    @apply before:content-[''];
    @apply before:absolute before:inset-[1px];
    @apply before:border;
    @apply before:border-primary-selected dark:before:border-opacity-40;

    &[data-copy] {
      @apply before:border-dashed;
    }
  }

  &-value {
    @apply relative;
    @apply flex-auto;
    @apply text-gray-400 dark:text-gray-500;
    @apply font-medium uppercase;
    @apply tracking-wider;
    @apply text-sm sm:text-xs;
    @apply text-ellipsis overflow-x-hidden;
  }

  &-resizer {
    @apply w-1.5;
    @apply absolute;
    @apply top-0.5 bottom-0.5 -right-0;
    @apply bg-primary-selected;
    @apply border-2 border-body;
    @apply cursor-col-resize;
    @apply opacity-10 hover:opacity-100;
    @apply z-30;
  }
}

.ui-grid:not(.ui-grid--collapsed) {
  .ui-grid__header {

    &--first {
      @apply pl-4 sm:pl-default;
    }

    &--last {
      @apply pr-4 sm:pr-default;
      @apply z-30 left-0;
    }

    &-value {
      &--right {
        @apply text-right;
      }
    }
  }
}
</style>
